import { Engine } from "@babylonjs/core/Engines/engine";
import { Scene } from "@babylonjs/core/scene";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { Vector3, Color3, Color4 } from "@babylonjs/core/Maths/math";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";

import { PARTS } from "./PartCatalog";
import { buildPartMesh } from "./PartMesh";

const cache = new Map<string, string>();

/** Render each part into a data-URL PNG thumbnail. Cached across calls. */
export async function ensurePartIcons(size = 96): Promise<Map<string, string>> {
  if (cache.size === PARTS.length) return cache;

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;

  // preserveDrawingBuffer must be true for toDataURL() to return rendered pixels.
  const engine = new Engine(canvas, true, { preserveDrawingBuffer: true, stencil: false }, true);

  for (const part of PARTS) {
    if (cache.has(part.id)) continue;

    const scene = new Scene(engine);
    // Dark blue-ish backdrop with a slight vignette — readable against the panel.
    scene.clearColor = new Color4(0.09, 0.12, 0.18, 1);

    // Per-part camera framing so each part fills its thumbnail.
    // Use the part's diagonal size as the basis; pad for tall/thin parts.
    const maxDim = Math.max(part.height, part.halfWidth * 2);
    const camDist = maxDim * 1.9 + 0.4;
    const camera = new ArcRotateCamera(
      "iconCam",
      -Math.PI / 4,   // azimuth — slight angled view
      Math.PI / 2.4,  // elevation — mostly side with a hint of top
      camDist,
      new Vector3(0, 0, 0),
      scene,
    );
    camera.minZ = 0.01;
    camera.maxZ = 100;
    scene.activeCamera = camera;

    // Three-point-ish lighting for legibility on a dark bg.
    const hemi = new HemisphericLight("h", new Vector3(0, 1, 0), scene);
    hemi.intensity = 0.55;
    hemi.groundColor = new Color3(0.05, 0.08, 0.14);
    const key = new DirectionalLight("k", new Vector3(-0.5, -0.7, -0.4), scene);
    key.intensity = 1.6;
    key.diffuse = new Color3(1, 0.96, 0.9);
    const rim = new DirectionalLight("rim", new Vector3(0.8, 0.3, 0.6), scene);
    rim.intensity = 0.55;
    rim.diffuse = new Color3(0.6, 0.8, 1);

    // Build the part and center it on origin.
    const group = buildPartMesh(scene, part, `icon_${part.id}`);
    group.position.y = 0;

    // Ground disc for shadow-like anchoring (non-essential but reads better)
    const disc = MeshBuilder.CreateDisc("iconDisc", { radius: maxDim * 0.9, tessellation: 24 }, scene);
    const dmat = new StandardMaterial("idm", scene);
    dmat.diffuseColor = new Color3(0.04, 0.06, 0.1);
    dmat.specularColor = new Color3(0, 0, 0);
    disc.material = dmat;
    disc.rotation.x = Math.PI / 2;
    disc.position.y = -part.height / 2 - 0.02;

    // Wait for the scene to be fully ready then render several frames to give
    // meshes time to upload vertex buffers and materials to GPU.
    await new Promise<void>((resolve) => scene.executeWhenReady(() => resolve()));
    for (let i = 0; i < 3; i++) scene.render();

    try {
      cache.set(part.id, canvas.toDataURL("image/png"));
    } catch {
      // ignored — canvas tainted (shouldn't happen here, no cross-origin content)
    }

    scene.dispose();
    await new Promise<void>((r) => setTimeout(r, 0));
  }

  engine.dispose();
  return cache;
}

export function getIcon(partId: string): string | undefined {
  return cache.get(partId);
}
